import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { VideoAction, VideoGetter } from '@/store/video/video.module';
import { VideoSchema } from '@/store/video/video.validator';
import { Video } from '@/store/video/video.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'VideoFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    videoId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => !!props.videoId);
    const video = computed<Video | undefined>(() => store.getters[VideoGetter.BY_ID](props.videoId));
    const title = computed(() => isEditMode.value ? 'แก้ไขวิดีโอ' : 'เพิ่มวิดีโอ');
    const videoForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      description?: string | null;
      pined?: boolean;
      url?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      description: '',
      pined: false,
      url: '',
    });
    const validator = computed(() => VideoSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      description: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'description')),
      url: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'url')),
    });
    const isValid = computed(() => !errors.title && !errors.description && !errors.url);
    const isLoading = computed(() => store.getters[VideoGetter.STATUS] === 'init' || store.getters[VideoGetter.STATUS] === 'loading');
    const submited = ref(false);


    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.videoId : null;
        formData.title = video.value?.title;
        formData.description = video.value?.description;
        formData.pined = (isEditMode.value) ? video.value?.pined : false;
        formData.url = video.value?.url;
        resolve();
      }).then(() => videoForm.value?.show());
    };

    // hide modal
    const hide = () => {
      videoForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.description = '';
        formData.pined = false;
        formData.url = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(VideoAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            description: formData.description,
            pined: formData.pined,
            url: formData.url,
          }).then(() => hide());
        } else {
          store.dispatch(VideoAction.CREATE, {
            title: formData.title,
            description: formData.description,
            pined: formData.pined,
            url: formData.url,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(VideoAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      videoForm,
      isEditMode,
      errors,
      submited,
      isLoading,
      show,
      hide,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
